import React, { useMemo } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';

import {
  BudgetMemberHoursSeries,
  buildBudgetMembersChartRows,
  getBudgetMembersChartYMax,
  getSeriesKey,
  OTHER_MEMBER_LINE_COLORS,
  CURRENT_USER_LINE_COLOR
} from '@/utils/budgetMembersChart';

interface BudgetMembersHoursChartProps {
  series: BudgetMemberHoursSeries[];
  currentUserId: string;
  className?: string;
}

const LastPointDot =
  (color: string, lastIndex: number) =>
  ({ cx, cy, index }: { cx?: number; cy?: number; index?: number }) => {
    if (cx == null || cy == null || index !== lastIndex) {
      return <g />;
    }

    return <circle cx={cx} cy={cy} r={5} fill={color} stroke="#ffffff" strokeWidth={2} />;
  };

const createTooltipContent =
  (series: BudgetMemberHoursSeries[]) =>
  ({ active, payload, label }: TooltipProps<number, string>) => {
    if (!active || !payload?.length) {
      return null;
    }

    const dateLabel = typeof label === 'string' ? label : String(label ?? '');

    return (
      <div
        className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-xs shadow-sm"
        style={{ fontSize: '12px' }}
      >
        <p className="mb-1 font-medium text-[#495057]">{dateLabel}</p>
        {payload.map((entry) => {
          const member = series.find((item) => getSeriesKey(item.userId) === String(entry.dataKey));
          const point = member?.dataPoints.find((dataPoint) => dataPoint.date === dateLabel);
          const cumulativeHours = Number(entry.value ?? 0);

          return (
            <p key={String(entry.dataKey)} className="text-[#495057]">
              <span className="font-medium">{member?.name ?? 'Member'}:</span>{' '}
              {point?.monthlyHours != null
                ? `${point.monthlyHours} hrs this month · ${cumulativeHours} cumulative`
                : `${cumulativeHours} hours`}
            </p>
          );
        })}
      </div>
    );
  };

const BudgetMembersHoursChart: React.FC<BudgetMembersHoursChartProps> = ({ series, currentUserId, className = '' }) => {
  const chartRows = useMemo(() => buildBudgetMembersChartRows(series), [series]);
  const yMax = useMemo(() => getBudgetMembersChartYMax(series), [series]);
  const yTicks = useMemo(() => Array.from({ length: yMax / 100 + 1 }, (_, index) => index * 100), [yMax]);
  const lastPointIndex = chartRows.length - 1;
  const tooltipContent = useMemo(() => createTooltipContent(series), [series]);

  if (!series.length || !chartRows.length) {
    return (
      <div className={`flex items-center justify-center min-h-[280px] text-sm text-secondary ${className}`}>
        No member hours data available yet.
      </div>
    );
  }

  return (
    <div className={`w-full h-full ${className}`}>
      <p className="text-xs text-secondary font-medium mb-2">hours</p>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartRows} margin={{ top: 12, right: 24, left: 4, bottom: 16 }}>
          <CartesianGrid stroke="#E5E7EB" strokeDasharray="4 4" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fill: '#93999F', fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            tickMargin={16}
            minTickGap={24}
          />
          <YAxis
            domain={[0, yMax]}
            ticks={yTicks}
            tick={{ fill: '#93999F', fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            tickMargin={10}
            width={40}
          />
          <Tooltip content={tooltipContent} />
          {series.map((member) => {
            const isCurrentUser = member.userId === currentUserId;
            const otherMemberIndex = series
              .filter((entry) => entry.userId !== currentUserId)
              .findIndex((entry) => entry.userId === member.userId);
            const color = isCurrentUser
              ? CURRENT_USER_LINE_COLOR
              : OTHER_MEMBER_LINE_COLORS[otherMemberIndex % OTHER_MEMBER_LINE_COLORS.length];
            const dataKey = getSeriesKey(member.userId);

            return (
              <Line
                key={member.userId}
                type="monotone"
                dataKey={dataKey}
                stroke={color}
                strokeWidth={member.userId === currentUserId ? 3 : 2}
                dot={LastPointDot(color, lastPointIndex)}
                activeDot={{ r: 6, strokeWidth: 2, stroke: '#ffffff' }}
                connectNulls
                isAnimationActive
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BudgetMembersHoursChart;
